import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { accountingApi, type LedgerAccount } from "@/api/accounting";
import { useAccountingHospitalContext } from "./AccountingHospitalContext";

export default function AccountingChartOfAccountsPage() {
  const { hospitalId, canPost } = useAccountingHospitalContext();
  const qc = useQueryClient();

  const { data: accounts = [], isLoading } = useQuery({
    queryKey: ["accounting-accounts", hospitalId],
    queryFn: () => accountingApi.listLedgerAccounts(hospitalId, false),
    enabled: !!hospitalId,
  });

  const seedMut = useMutation({
    mutationFn: () => accountingApi.seedCoa(hospitalId),
    onSuccess: () => {
      toast.success("System CoA seeded");
      qc.invalidateQueries({ queryKey: ["accounting-accounts", hospitalId] });
      qc.invalidateQueries({ queryKey: ["accounting-dashboard", hospitalId] });
    },
    onError: (e: { response?: { data?: { message?: string } } }) =>
      toast.error(e.response?.data?.message || "Seed failed"),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-lg font-medium">Chart of accounts</h2>
        {canPost ? (
          <Button
            size="sm"
            variant="outline"
            disabled={seedMut.isPending}
            onClick={() => seedMut.mutate()}
          >
            Seed / refresh system CoA
          </Button>
        ) : null}
      </div>
      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Normal</TableHead>
              <TableHead>Group</TableHead>
              <TableHead>Flags</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6}>Loading…</TableCell>
              </TableRow>
            ) : (accounts as LedgerAccount[]).length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-slate-500">
                  No accounts. Seed the system chart of accounts to begin.
                </TableCell>
              </TableRow>
            ) : (
              (accounts as LedgerAccount[]).map((a) => (
                <TableRow key={a.id}>
                  <TableCell className="font-mono text-sm">{a.code}</TableCell>
                  <TableCell>{a.name}</TableCell>
                  <TableCell>{a.accountType}</TableCell>
                  <TableCell>{a.normalBalance}</TableCell>
                  <TableCell>{a.ledgerGroup?.name || "—"}</TableCell>
                  <TableCell className="space-x-1">
                    {a.isSystem ? <Badge variant="secondary">System</Badge> : null}
                    {!a.active ? <Badge variant="outline">Inactive</Badge> : null}
                    {!a.isPostable ? <Badge variant="outline">Non-postable</Badge> : null}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
